import React, { useEffect, useState } from "react";

type InputFieldDebounceProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  name?: string;
  type?: string;
  rightIcon?: React.ReactNode;
  autoFocus?: boolean;
  required?: boolean;
  mode?: "input" | "textarea";
  rows?: number;
  resize?: boolean;
  delay?: number;
  testId?: string;
};

const InputFieldDebounce = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  errorMessage,
  className,
  name,
  type = "text",
  rightIcon,
  autoFocus,
  required,
  mode = "input",
  rows = 3,
  resize = false,
  delay = 400,
  testId,
}: InputFieldDebounceProps) => {
  const [internalValue, setInternalValue] = useState(value || "");

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (internalValue !== (value || "")) {
        if (onChange) onChange(internalValue);
      }
    }, delay);
    return () => clearTimeout(handler);
  }, [internalValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => {
    if (onChange) onChange(internalValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (onChange) onChange(internalValue);
    }
  };

  return (
    <div className={`w-full ${className || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        {mode === "textarea" ? (
          <textarea
            data-testid={testId}
            autoFocus={autoFocus}
            name={name}
            value={internalValue}
            onChange={(e) => setInternalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            rows={rows}
            className={`w-full px-3 py-2 -mb-1 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400 ${
              resize ? "resize-y" : "resize-none"
            }`}
          />
        ) : (
          <input
            data-testid={testId}
            autoFocus={autoFocus}
            type={type}
            name={name}
            value={internalValue}
            onChange={(e) => setInternalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            className={`w-full ${
              rightIcon ? "pr-10" : ""
            } px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400`}
          />
        )}
        {rightIcon && mode === "input" ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
            {rightIcon}
          </span>
        ) : null}
      </div>
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
};

export default InputFieldDebounce;
